/**
 * The global combat + movement tuning tables.
 *
 * The single source of truth is `combat.store` (repo root); this module
 * imports its generated projection `combat.data.ts` (do not edit — save in
 * `npm run knobs` or run `node tools/combat-store.js`).
 *
 * WHY THE SIM READS THESE FROM STATE, NOT FROM HERE: the dev game server
 * reads the live store when a floor STARTS and passes the tables into
 * `createState` as recorded setup (`State.combat` — the same pattern as the
 * injectable item/NPC catalogs), so a knob edit is in effect on the next
 * floor with no rebuild (docs/design/knobs.md). `COMBAT` below is the
 * committed default: headless tools (scenarios, fuzz, arena, tests) and any
 * run that doesn't inject a table run at these numbers.
 *
 * The parser (`parseCombatStore`) is pure text-in / tables-out with NO fs
 * and NO dependency on this module's own generated data — `tools/` and
 * `net/` both import it, and a bad store is a validation error at save,
 * never a runtime surprise. TICK_MS is deliberately NOT here: it is a
 * wipe-level constant (invalidates every replay), so it stays a rule in
 * constants.ts, not a knob.
 */
import { COMBAT_DATA } from "./combat.data.js";
import type { CombatStyle, NpcDef, NpcStats } from "./npcs.js";
import type { WeaponClass } from "./items.js";

/** The knob's unit (the knobs app's display + validation). */
export interface CombatField {
  /** Store key — the matching CombatTables field name. */
  key: string;
  /** Display name in the knobs app. */
  label: string;
  unit: "ticks" | "percent" | "int";
  min: number;
  max: number;
  /** Changing this regenerates scenario fixtures / the solver proof (the
   *  knobs app shows a warning badge). */
  fixtureInvalidating?: boolean;
  /** One-line hint under the field in the knobs app. */
  hint?: string;
}

/** The global tuning tables — all integers (AGENTS §3). Durations are ticks
 *  (20 = 1 s); percents are ×100 (100 = neutral / 100%). The hero's max HP
 *  is NOT here — it lives in the class level table (LevelRow.maxHp,
 *  classes.store): the per-level knob, one number per level, armor adds on. */
export interface CombatTables {
  /** Hero walk cadence, ticks per tile (no speed buff). Lower = faster. */
  moveCostTicks: number;
  /** Tiles per room block side (rooms are N×N floor blocks + 1-tile strips). */
  roomTiles: number;
  /** Max keys + carried stones + unequipped items in one inventory. */
  inventoryCap: number;
  /** Max chat message length (chars); longer is truncated. */
  chatMax: number;
  /** Death → bones → respawn at the base, in ticks. */
  respawnDelayTicks: number;
  /** Bare-hero ATTACK (contest points — the weapon's `attack` replaces
   *  this; the class level's atkBonus adds on). A weapon is worth 10–100;
   *  fists are the underdog by design. */
  baseAttack: number;
  /** Bare-hero defense POINTS per bucket (armor `meleeDef`/`magicDef` add
   *  on; these are what an unarmed, unarmored hero brings to the contest). */
  baseMeleeDef: number;
  baseMagicDef: number;
  /** Bare-hero weapon damage range, inclusive. */
  baseAtkMin: number;
  baseAtkMax: number;
  /** Bare-hero swing cadence, ticks. */
  baseSwingTicks: number;
  /** Hero damage modifier, ×100 percents (100 = ×1.0). */
  baseDmgMod: number;
  /** Hit-chance floor, percent. The weakest legal matchup still lands
   *  roughly 1 in 100/floor swings — a wall is structurally impossible. */
  accFloorPct: number;
  /** Hit-chance cap, percent. "Cannot miss" is authoritatively impossible,
   *  so the silent-miss animation always has a job. */
  accCapPct: number;
}

/**
 * THE ACCURACY CONTEST (the two-roll model's roll 1 — see constants.ts).
 * Attack and defense are POINTS on the same scale; hit chance is the
 * smooth contest curve, in integer basis points:
 *
 *   chance = floor + (cap − floor) × attack / (attack + defense)
 *
 * attack ≤ 0 never hits (a zero-attack creature is harmless by authoring,
 * not by a special case elsewhere); defense < 0 clamps to 0. Attack ≫
 * defense asymptotes to the cap; defense ≫ attack asymptotes to the floor;
 * equal values sit above the midpoint (the contest is gently biased to the
 * attacker). One integer division — no floats, no transcendental math
 * (AGENTS §3). PURE and shared: the sim rolls with it, the arena stat line
 * and the knobs DPS tab draw with it, so a tuning surface can never drift
 * from the authority.
 */
export function hitChanceBps(
  attack: number,
  defense: number,
  floorPct: number,
  capPct: number,
): number {
  if (attack <= 0) return 0;
  const d = Math.max(0, defense);
  return floorPct * 100 + Math.floor(((capPct - floorPct) * 100 * attack) / (attack + d));
}

/** The defender's CONTEST BUCKET for an attack class: magic rolls vs
 * magicDef; melee and the reserved ranged roll vs meleeDef (an arrow is
 * physical — you shield it, not ward it). THE rule, shared by the sim's
 * `defBucket`, the duel preview and every display surface — the bucket pick
 * is never re-implemented at a call site. */
export function defBucketOf(meleeDef: number, magicDef: number, atkClass: WeaponClass): number {
  return atkClass === "magic" ? magicDef : meleeDef;
}

/** The field table — the validation bounds AND the knobs app's render
 *  descriptor in one place (the same data the store file documents). */
export const COMBAT_FIELDS: CombatField[] = [
  { key: "moveCostTicks", label: "Walk speed", unit: "ticks", min: 1, max: 30, fixtureInvalidating: true, hint: "how long one tile takes to cross (the app shows ms — 50 ms per tick); lower is faster" },
  { key: "roomTiles", label: "Room size", unit: "int", min: 4, max: 16, fixtureInvalidating: true, hint: "tiles per room side — rooms are N×N floor blocks plus 1-tile strips" },
  { key: "inventoryCap", label: "Inventory size", unit: "int", min: 1, max: 64, fixtureInvalidating: true, hint: "max keys + gatestones + unequipped items you can carry (loot drops are never blocked)" },
  { key: "chatMax", label: "Chat length", unit: "int", min: 1, max: 1000, hint: "max characters per chat message" },
  { key: "respawnDelayTicks", label: "Time to respawn", unit: "ticks", min: 0, max: 3600, hint: "how long you lie in bones before walking again (60 ticks = 3 s)" },
  { key: "baseAttack", label: "Fist attack", unit: "int", min: 0, max: 1000, hint: "your attack with no weapon (a weapon's attack replaces this; the level bonus adds on) — weapons are worth ~10–100" },
  { key: "baseMeleeDef", label: "Base melee defense", unit: "int", min: 0, max: 1000, hint: "defense POINTS against melee+ranged attacks with no armor (armor adds on) — points contest the attacker's attack, they never reduce damage" },
  { key: "baseMagicDef", label: "Base magic defense", unit: "int", min: 0, max: 1000, hint: "defense POINTS against magic attacks with no armor (armor adds on) — same contest, different bucket" },
  { key: "baseAtkMin", label: "Fist damage (min)", unit: "int", min: 1, max: 1000, hint: "damage with no weapon — a weapon's damage replaces this" },
  { key: "baseAtkMax", label: "Fist damage (max)", unit: "int", min: 1, max: 1000 },
  { key: "baseSwingTicks", label: "Attack speed", unit: "ticks", min: 1, max: 200, hint: "how long one swing takes with no weapon" },
  { key: "baseDmgMod", label: "Damage modifier", unit: "percent", min: 1, max: 1000, hint: "×100 — 100 is neutral (×1.0), 120 is +20%; the class level scales this" },
  { key: "accFloorPct", label: "Hit chance floor", unit: "percent", min: 0, max: 100, hint: "the weakest attack vs the strongest defense still hits this often — no matchup can ever become a wall" },
  { key: "accCapPct", label: "Hit chance cap", unit: "percent", min: 1, max: 100, hint: "even overwhelming attack never hits more often than this — misses always stay in the feel" },
];

/** Validate a complete table (throws — for the module-load check and
 *  `withCombat`; the text parser reports line-numbered errors instead). */
export function validateCombat(t: CombatTables): CombatTables {
  for (const f of COMBAT_FIELDS) {
    const v = (t as unknown as Record<string, unknown>)[f.key];
    if (typeof v !== "number" || !Number.isInteger(v)) {
      throw new Error(`combat: ${f.key} must be an integer (got ${String(v)})`);
    }
    if (v < f.min || v > f.max) {
      throw new Error(`combat: ${f.key} = ${v} out of range [${f.min}, ${f.max}]`);
    }
  }
  if (t.baseAtkMin > t.baseAtkMax) {
    throw new Error(`combat: baseAtkMin (${t.baseAtkMin}) > baseAtkMax (${t.baseAtkMax})`);
  }
  if (t.accFloorPct > t.accCapPct) {
    throw new Error(`combat: accFloorPct (${t.accFloorPct}) > accCapPct (${t.accCapPct})`);
  }
  return t;
}

/** The committed default tables (combat.store's projection) — validated at
 *  module load: a bad store is a build error, never a runtime surprise. */
export const COMBAT: CombatTables = validateCombat({ ...COMBAT_DATA });

/**
 * Parse + validate combat.store text. PURE (no fs, no read of COMBAT_DATA's
 * values — only COMBAT_FIELDS' bounds), so `tools/combat-store.js` (which
 * emits combat.data.ts) and `net/` (which reads the live store at floor
 * start) both import it without a stale-projection cycle.
 * Errors are line-numbered, like the other store parsers.
 */
export function parseCombatStore(text: string): { ok: true; tables: CombatTables } | { ok: false; errors: string[] } {
  const errors: string[] = [];
  const tables = {} as Record<string, number>;
  let inBlock = false;
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const line = lines[i]!.trim();
    if (line === "" || line.startsWith("#")) continue;
    if (line === "[combat]") {
      if (inBlock) errors.push(`line ${lineNo}: duplicate [combat] block`);
      inBlock = true;
      continue;
    }
    if (/^\[/.test(line)) {
      errors.push(`line ${lineNo}: unknown block "${line}" (the only block is [combat])`);
      inBlock = false;
      continue;
    }
    if (!inBlock) {
      errors.push(`line ${lineNo}: field outside the [combat] block`);
      continue;
    }
    const kv = /^([a-zA-Z][a-zA-Z0-9]*)\s*;\s*(.*)$/.exec(line);
    if (!kv) {
      errors.push(`line ${lineNo}: not a "key ; value" line: ${line}`);
      continue;
    }
    const key = kv[1]!;
    const field = COMBAT_FIELDS.find((f) => f.key === key);
    if (!field) {
      errors.push(`line ${lineNo}: unknown key "${key}" (known: ${COMBAT_FIELDS.map((f) => f.key).join(", ")})`);
      continue;
    }
    if (tables[key] !== undefined) {
      errors.push(`line ${lineNo}: duplicate key "${key}"`);
      continue;
    }
    const value = kv[2]!.trim();
    if (!/^-?\d+$/.test(value)) {
      errors.push(`line ${lineNo}: ${key} must be an integer (got "${value}")`);
      continue;
    }
    tables[key] = Number(value);
  }
  if (!inBlock && !errors.some((e) => e.includes("duplicate [combat]"))) {
    errors.push("missing the [combat] block header");
  }
  for (const f of COMBAT_FIELDS) {
    if (tables[f.key] === undefined) errors.push(`missing key "${f.key}"`);
  }
  if (errors.length > 0) return { ok: false, errors };
  const t = tables as unknown as CombatTables;
  try {
    validateCombat(t);
  } catch (e) {
    errors.push(String((e as Error).message));
    return { ok: false, errors };
  }
  return { ok: true, tables: t };
}

/** Canonical serialization (the knobs app's Save output — one field per line
 *  in COMBAT_FIELDS order, so git diffs read `respawnDelayTicks 60→80`). */
export function serializeCombatStore(t: CombatTables): string {
  const out: string[] = [];
  out.push("# combat.store — the global combat + movement tuning tables (the single source of truth).");
  out.push("#");
  out.push("# Edit by hand or via `npm run knobs` (the Combat tab). `tools/combat-store.js`");
  out.push("# parses + validates this file and emits packages/shared/src/combat.data.ts");
  out.push("# (generated, committed — never edit that one).");
  out.push("#");
  out.push("# Units (AGENTS §3/§4): all integers. Durations are TICKS (20 = 1 s);");
  out.push("# percents are ×100 (100 = neutral / 100%).");
  out.push("#");
  out.push("# The dev game server reads this file when a floor STARTS, so a save is");
  out.push("# in effect on the next floor with no rebuild (docs/design/knobs.md).");
  out.push("#");
  out.push("[combat]");
  for (const f of COMBAT_FIELDS) {
    if (f.hint) out.push(`# ${f.hint}${f.fixtureInvalidating ? " [fixture-invalidating]" : ""}`);
    out.push(`${f.key} ; ${t[f.key as keyof CombatTables]}`);
  }
  return out.join("\n") + "\n";
}

/** Merge a partial table over the committed defaults and validate it — the
 *  entry point for run setup (scenario `config.combat`, net's live read). */
export function withCombat(partial?: Partial<CombatTables>): CombatTables {
  return validateCombat({ ...COMBAT, ...partial });
}

/* --------------------------------------------------------------------------
 * THE DERIVED NPC COMBAT LEVEL — display only, never a sim input
 * ------------------------------------------------------------------------ */

/** An NPC with no authored `maxHp` is this tough — THE fallback, shared with
 *  the sim's `enemyStats` so what an unauthored HP stat means can never
 *  disagree between the fight and the level readout. */
export const NPC_HP_FALLBACK = 10;

/**
 * The threat weights: threat POINTS per unit of each term, and the divisor
 * that converts points to levels. Integer-only, like everything else.
 *
 * The shape of the sum is "what it can deal, plus how long it takes to kill,
 * plus its edge" — the three things a player is actually reacting to.
 *
 * - `dps` × the NPC's expected DPS ×1000 — THE DUEL MATH (`hitChanceBps`
 *   against the bare hero's bucket for this NPC's style, the sim's own damage
 *   expansion, over the swing cadence: the same algebra as `dpsMilli` in
 *   sim/dps.ts, which for an NPC with no damage bonus and full weapon
 *   efficiency is exactly `hitBps × (atkMin + atkMax) / swingTicks`). This is
 *   the ONLY way `attack` enters: as a real contest chance, not raw points,
 *   so 75 attack is not read as 3.75× worse than 20 when the curve only moves
 *   45% → 69%. Cadence lives here too, which is what makes a slow swing cost
 *   the level something.
 * - `hp` × hit points, with a knee: above `hpKnee` each point counts at
 *   1/`hpTail`. HP is the one term a human tunes into the thousands, and
 *   without the knee a 1000-HP boss either swallows the scale or squashes
 *   every other monster into one digit. Below the knee nothing is compressed.
 * - `arm` × the AVERAGE of the two defense buckets — a player experiences
 *   "hard to hit", not two buckets, and defense changes how OFTEN it is hit
 *   exactly as the contest says it should.
 * - `reach` × tiles beyond 1 (a 4-tile caster is worse than a 1-tile one),
 *   plus `rush` × ticks per tile FASTER than the hero's walk cadence (no
 *   authored NPC is faster today; a `moveSpeedTicks: 3` rusher scores).
 *
 * `divisor` authors the absolute scale: today's roster reads 21 (goblin) to
 * 162 (Luminak). That is a THREAT ORDINAL, not a hero comparison — there is
 * no hero combat level any more; the tone thresholds below are what make the
 * number legible.
 */
export const NPC_LEVEL_WEIGHTS = {
  /** per milli-DPS (expected damage per second ×1000 — the dpsMilli scale). */
  dps: 1,
  /** per hitpoint, up to the knee. */
  hp: 25,
  /** the knee: hit points up to here count at full weight. */
  hpKnee: 250,
  /** hit points above the knee count at 1/hpTail. */
  hpTail: 4,
  /** per point of AVERAGED defense ((meleeDef + magicDef) / 2). */
  arm: 50,
  /** per tile of reach beyond 1. */
  reach: 100,
  /** per tick per tile faster than the hero's walk cadence. */
  rush: 25,
  /** threat points per level. */
  divisor: 100,
} as const;

/** The derived combat level of an NPC's stat line (see NPC_LEVEL_WEIGHTS for
 *  the shape and the scale). PURE function of the stats, the combat style and
 *  the RUN's tables — so a knob edit to the accuracy contest or the bare-hero
 *  numbers retunes the readout on the next floor, like the fight itself. It
 *  is NEVER read by the sim: no roll, no hash, no replay (that module is the
 *  authority for the rolls; this one only describes them). Absent stats fall
 *  back exactly as `enemyStats` does, so an unauthored stat means the same
 *  thing here as in the fight. Integer arithmetic only (AGENTS §3). */
export function npcCombatLevel(
  stats: NpcStats,
  style: CombatStyle,
  tables: CombatTables,
): number {
  const W = NPC_LEVEL_WEIGHTS;
  const atkMin = stats.atkMin ?? tables.baseAtkMin;
  const atkMax = stats.atkMax ?? tables.baseAtkMax;
  const swing = Math.max(1, stats.speedTicks ?? tables.baseSwingTicks);
  const attack = stats.attack ?? tables.baseAttack;
  const meleeDef = stats.meleeDef ?? 0;
  const magicDef = stats.magicDef ?? 0;
  const range = stats.range ?? 1;
  const maxHp = stats.maxHp ?? NPC_HP_FALLBACK;
  const move = stats.moveSpeedTicks ?? tables.moveCostTicks;

  // OFFENCE — the duel math: the contest chance vs the bare hero's bucket for
  // THIS style, the damage expansion, over the swing cadence. ×1000.
  const bucket = style === "magic" ? tables.baseMagicDef : tables.baseMeleeDef;
  const accBps = hitChanceBps(attack, bucket, tables.accFloorPct, tables.accCapPct);
  const expandedMax = Math.max(atkMin, Math.floor((atkMax * tables.baseDmgMod) / 100));
  const dpsMilli = Math.floor((accBps * (atkMin + expandedMax)) / swing);

  // SURVIVABILITY — hit points (knee-compressed) plus the defense that soaks
  // hits with them.
  const life = maxHp > W.hpKnee
    ? W.hpKnee + Math.floor((maxHp - W.hpKnee) / W.hpTail)
    : maxHp;
  const arm = Math.floor((meleeDef + magicDef) / 2);

  // EDGE — reach beyond melee, and walking faster than the hero.
  const edge = Math.max(0, range - 1) * W.reach
    + Math.max(0, tables.moveCostTicks - move) * W.rush;

  return 1 + Math.floor((dpsMilli * W.dps + life * W.hp + arm * W.arm + edge) / W.divisor);
}

/** The DISPLAY level of an NPC: the store's `combatLevel` when the human
 *  authored one — an explicit OVERRIDE, after which retuning that entry's
 *  stats will NOT move the number — otherwise derived. Null for a kind the
 *  catalog doesn't know (a fixture, or an entry deleted mid-run): show no
 *  number rather than invent one. EVERY level surface — the attack verb, the
 *  arena select, the knobs floors preview — goes through this, so an override
 *  can never be honoured by one surface and ignored by another. */
export function npcDisplayLevel(def: NpcDef | undefined, tables: CombatTables): number | null {
  if (!def) return null;
  const override = def.stats.combatLevel;
  if (override !== undefined) return override;
  return npcCombatLevel(def.stats, def.style ?? "melee", tables);
}

/** Below this, a level tag is green. */
export const LEVEL_TONE_SAFE_MAX = 35;
/** At or above the green line and below this, yellow; at or above it, red.
 *  Tuned against the derived band — today's roster reads 6 green / 1 yellow /
 *  4 red, with the boss more than double the hardest elite. */
export const LEVEL_TONE_WARN_MAX = 60;

/** The level tag's colour class — the whole point of having a number. */
export type CombatLevelTone = "safe" | "warn" | "danger";

export function combatLevelTone(level: number): CombatLevelTone {
  if (level < LEVEL_TONE_SAFE_MAX) return "safe";
  if (level < LEVEL_TONE_WARN_MAX) return "warn";
  return "danger";
}
